import * as THREE from 'three';
import { MARKERS } from '../../config/tuning';

/**
 * Line geometry for the world markers — the checkpoint ring and the target beacon (SPEC §5).
 *
 * No external assets exist in this project, so these are built from vertex positions directly.
 * Lines rather than solid geometry: a marker is a *reference*, not an object in the theatre, and a
 * lit surface would read as something the drone could hit.
 *
 * Both shapes are generated at unit scale about their own origin and scaled by the renderer, so a
 * checkpoint whose capture radius the mission overrode reuses the same buffer.
 *
 * Metres throughout. +y is up.
 */

/**
 * A horizontal ring of unit radius in the xz plane, as `LineSegments` pairs.
 *
 * Segments rather than a closed `LineLoop` because the renderer draws the ring, the post and the
 * ticks from one geometry — one draw call for the whole marker.
 */
function ringSegments(radius: number, y: number, segments: number, into: number[]): void {
  for (let i = 0; i < segments; i += 1) {
    const a = (i / segments) * Math.PI * 2;
    const b = ((i + 1) / segments) * Math.PI * 2;
    into.push(Math.cos(a) * radius, y, Math.sin(a) * radius);
    into.push(Math.cos(b) * radius, y, Math.sin(b) * radius);
  }
}

/**
 * The checkpoint marker: a ring of unit radius with four inward ticks at the cardinals.
 *
 * The ring *is* the capture sphere — the renderer scales it by the objective's own `radiusM` — so
 * flying through it is passing the objective rather than approximately passing it. The ticks give
 * it an orientation cue at range, where the circle itself is only a few pixels across.
 */
export function createCheckpointGeometry(): THREE.BufferGeometry {
  const points: number[] = [];
  ringSegments(1, 0, MARKERS.ringSegments, points);

  for (let i = 0; i < 4; i += 1) {
    const a = (i / 4) * Math.PI * 2;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    points.push(cos, 0, sin);
    points.push(cos * (1 - MARKERS.tickFraction), 0, sin * (1 - MARKERS.tickFraction));
  }

  return fromPoints(points);
}

/**
 * The post that hangs the checkpoint ring on a place: one unit-length segment running downward.
 *
 * Separate from the ring, and separately scaled, because its length is the ring's height above the
 * *terrain* rather than a fraction of its radius. Built into the ring at a fixed proportion it
 * looked right over land and ran on below the waterline at a checkpoint out at sea — which is most
 * of them, on a coastal ingress.
 */
export function createPostGeometry(): THREE.BufferGeometry {
  return fromPoints([0, 0, 0, 0, -1, 0]);
}

/**
 * The target beacon: a vertical column standing on the target's base, with a ring at its foot.
 *
 * Vertical because a column is the one shape that stays legible from a nose camera at any bearing
 * and at any range — a ring seen edge-on disappears, a column never does. The foot ring marks the
 * ground position the column belongs to, which is what stops it reading as a distant tower.
 *
 * Metres, in world scale: unlike the checkpoint this is not scaled by the renderer, because a
 * target's marker should be the same size whatever the target is.
 */
export function createBeaconGeometry(): THREE.BufferGeometry {
  const points: number[] = [];
  ringSegments(MARKERS.beaconFootRadiusM, 0, MARKERS.ringSegments, points);

  // The column, in two segments with a gap, so it does not read as a solid mast.
  const height = MARKERS.beaconHeightM;
  points.push(0, 0, 0);
  points.push(0, height * MARKERS.beaconGapStart, 0);
  points.push(0, height * MARKERS.beaconGapEnd, 0);
  points.push(0, height, 0);

  // A cap: two short crossed bars at the top, giving the column a head to sight on.
  const cap = MARKERS.beaconCapM;
  points.push(-cap, height, 0);
  points.push(cap, height, 0);
  points.push(0, height, -cap);
  points.push(0, height, cap);

  return fromPoints(points);
}

function fromPoints(points: number[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  return geometry;
}
